/**
 * Plan / pricing rendering helpers — shared between
 * pages/System/Billing/SystemBilling.tsx and the TopNav Upgrade
 * dialog (components/navigation/TopNav.tsx).
 *
 * Operator console twin: svdy-website/src/components/billing/planMeta.ts
 * (Apple-style AdminStatusChip variant of the same chip-tone contract).
 * Both files share the canonical 4-tier color scheme + plan-code parsing
 * rules per locked invariant #15 (NEXT_SESSION_PROMPT 2026-05-22).
 * Update in lockstep when adding new tiers / changing colors.
 *
 * Why a shared module: before this file existed, both surfaces
 * had to render plan cards with the same data shape, but the
 * TopNav 'Choose your plan' dialog used a free-form features
 * array (legacy) while SystemBilling rendered the structured
 * metering grid that matches what the license middleware actually
 * enforces. Tenants saw two different feature lists for the same
 * Plan row depending on which entry point they opened.
 *
 * This module exposes:
 *  - PRODUCT_CHIP_META: licensed_products → chip palette
 *  - PlanFeatureRow: the check-marked one-line metering row
 *  - getPlanDisplayName/getPlanDescription/getPlanHighlightLabel:
 *    localized canonical metadata with legacy/custom fallback
 *  - getPlanFeatureLabels/getPlanBullets: localized structured
 *    capabilities for canonical plan cards
 *
 * Both consumers pass their i18next translator into these helpers so
 * canonical backend English never becomes the primary display copy.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';

import type { Plan } from '../../api/subscription';


/**
 * licensed_products → chip palette for the colored brand chips
 * surfaced on plan cards. Stays in sync with the Tenant Plans editor
 * + Plan picker on /admin/billing/plans.
 *
 * Color choices: QuTime borrows the attendance/time-blue palette
 * (#1976d2); PureOA borrows the process/workflow-purple palette
 * (#7b1fa2). They line up with the icons used in the navigation
 * sidebar and System Overview cards.
 */
export const PRODUCT_CHIP_META: Record<string, { label: string; color: string; bg: string }> = {
  qutime: {
    label: 'QuTime',
    color: '#0d47a1',
    bg: 'rgba(25,118,210,0.12)',
  },
  pureoa: {
    label: 'PureOA',
    color: '#4a148c',
    bg: 'rgba(123,31,162,0.12)',
  },
};


export type PlanTranslator = (
  key: string,
  options?: Record<string, unknown>,
) => string;

/** Canonical catalog copy must come from locale files, not backend English. */
export function getPlanDisplayName(plan: Plan, t: PlanTranslator): string {
  if (!isCanonicalPlan(plan)) return plan.name ?? plan.code ?? '—';
  const product = t(`common:planCard.products.${getProductLine(plan)}`);
  const tier = t(`common:planCard.tiers.${plan.tier ?? 'free'}`);
  return t('common:planCard.planName', { product, tier });
}

export function getPlanDescription(plan: Plan, t: PlanTranslator): string {
  if (!isCanonicalPlan(plan)) return plan.description ?? '';
  return t(`common:planCard.descriptions.${plan.tier ?? 'free'}`);
}

export function getPlanHighlightLabel(plan: Plan, t: PlanTranslator): string | null {
  if (isCanonicalPlan(plan)) {
    return plan.tier === 'professional' ? t('common:topNav.mostPopular') : null;
  }
  return plan.highlight_label ?? null;
}


/** Single check-marked metering line on a plan card. */
/** The row's type + glyph metrics. No offset is derived from them any more — see `PlanFeatureRow`. */
const PLAN_FEATURE_FONT_SIZE = '0.72rem';
const PLAN_FEATURE_LINE_HEIGHT = 1.5;
const PLAN_FEATURE_GLYPH_PX = 14;

/** One ticked feature row, optionally with a CONTINUATION line under its label.
 *
 * CSS GRID, after three rounds of "还没对齐" that were each fixed by tuning a number. The owner's
 * instruction was the right diagnosis: 重构代码实现方式，不要给我量了.
 *
 * What flexbox made into arithmetic, and got wrong twice each:
 *
 *   HORIZONTAL — with glyph and text as flex siblings, anything else that must sit on the text edge
 *   (the `then $N/worker/mo` continuation) has to reproduce `glyph + gap` by hand. Written as
 *   `ml: '22px'` against a real 20px, then as a shared constant that still had to be read at the
 *   call site.
 *
 *   VERTICAL — `align-items: flex-start` puts a 14px glyph at the TOP of a 17.28px line box, so it
 *   floats above the text and needs `(box − glyph) / 2` added back. Hand-written as `2px` against a
 *   real 1.64px.
 *
 * Grid removes both. `auto 1fr` puts the glyph in column 1 and ALL text in column 2 — first line,
 * wrapped lines, and the continuation — so there is exactly one text edge and nothing computes it.
 * The glyph sits in a box exactly ONE LINE tall (`${PLAN_FEATURE_LINE_HEIGHT}em` at the row's own
 * font size) and is centred in it, so it lands on the first line's optical centre by construction.
 *
 * Twin: `svdy-website/src/views/Pricing/index.tsx::FeatureRow`, same technique. The two surfaces
 * show the same list to the same buyer and must not drift (svdy-ui §13).
 */
export const PlanFeatureRow: React.FC<{ label: string; note?: string }> = ({ label, note }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 0.75 }}>
    <Box
      aria-hidden
      sx={{
        fontSize: PLAN_FEATURE_FONT_SIZE,
        height: `${PLAN_FEATURE_LINE_HEIGHT}em`,
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <CheckCircleOutlined sx={{ fontSize: PLAN_FEATURE_GLYPH_PX, color: 'success.main' }} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontSize: PLAN_FEATURE_FONT_SIZE,
          lineHeight: PLAN_FEATURE_LINE_HEIGHT,
        }}
      >
        {label}
      </Typography>
      {note && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            fontSize: '0.68rem',
            lineHeight: PLAN_FEATURE_LINE_HEIGHT,
          }}
        >
          {note}
        </Typography>
      )}
    </Box>
  </Box>
);

/** The vertical gap the card puts BETWEEN feature items, in px.
 *
 * The gap between two items must exceed the WHITESPACE between two lines inside one item, or a
 * wrapped item and its neighbour merge. At 0.72rem × 1.5 the internal leading is ~5.8px, so 8px
 * clears it. `spacing={0.5}` (4px) did not, which is why the list read as continuous.
 *
 * No longer negated by anything: a continuation line lives INSIDE its row now, so there is no
 * sibling boundary to cancel. Exported so `planFeatureRowAlignment.test` can pin the pair with the
 * card's `Stack`.
 */
export const PLAN_FEATURE_ROW_GAP_PX = 8;



/**
 * Compute the ordered list of labels to render on a plan card from
 * the structured metering columns (PRODUCT_SUBSCRIPTION_STRATEGY §6
 * Phase B). Mirrors the manual switch ladder in SystemBilling
 * verbatim so both consumers (TopNav + SystemBilling) render
 * identical bullet points.
 *
 * Boolean columns (advanced_reports / sso_enabled / multi_currency /
 * whitelabel) and unset numerics (max_integrations === 0,
 * api_access_level === 'none') are skipped — those rows would
 * otherwise read 'No advanced reports' / 'No API access' which is
 * noise on a card meant to highlight inclusions.
 */
/**
 * Support response-time SLA per `support_tier`, in hours.
 *
 * Twin of the map in svdy-website `src/views/Pricing/planCopy.ts`; the two must agree or the
 * public price list and the tenant's own Change-Plan card promise different response times for
 * the plan they are already paying for. The full reasoning lives in the website file — in short,
 * every tier can open a support case (`POST /v1/support/cases` has no plan gate anywhere), so the
 * differentiator is how fast we answer, and the four bare labels this replaced said otherwise.
 *
 * The hours live in code rather than in nine locale files so a number cannot drift per language.
 */
const SUPPORT_RESPONSE_HOURS: Record<string, number> = {
  community: 48,
  email: 24,
  priority: 8,
  dedicated: 4,
};

/** Which locale sentence a tier's support line uses. `dedicated` names a person, not a queue. */
const SUPPORT_SHAPE: Record<string, 'standard' | 'priority' | 'dedicated'> = {
  community: 'standard',
  email: 'standard',
  priority: 'priority',
  dedicated: 'dedicated',
};

/** Product line of a plan, from its licensed products. Attendance-only lines must not appear on
 *  a PureOA card — the defect that opened the 2026-08-31 pricing audit. */
function planLine(plan: Plan): 'qutime' | 'pureoa' | 'bundle' | null {
  const products = (plan as { licensed_products?: string[] }).licensed_products ?? [];
  if (products.length >= 2) return 'bundle';
  if (products[0] === 'qutime') return 'qutime';
  if (products[0] === 'pureoa') return 'pureoa';
  return null;
}

export function getPlanFeatureLabels(plan: Plan, t: PlanTranslator): string[] {
  const labels: string[] = [];

  // NO BRANCH COUNT, NO ATTENDANCE-HISTORY WINDOW (2026-08-31, owner).
  //
  // Both were real, enforced limits — `enforce_branch_limit` and a visibility window keyed on
  // `attendance_history_days` — and both were withdrawn as PRODUCTS rather than as claims:
  // branch count describes company shape, which this product already prices once by headcount,
  // and a tenant's attendance history is their own payroll and dispute evidence. The gates are
  // gone from svdy-platform in the same change (see the notes in `app/core/licensing.py`), and
  // alembic 116 sets both columns to their no-limit values on all 12 canonical plans.
  //
  // The 999 sentinel this file used to read went with the branch line; `UNLIMITED_SENTINEL` is
  // therefore gone from both surfaces rather than left as a constant nobody reads.

  const line = planLine(plan);
  const coversAttendance = line === 'qutime' || line === 'bundle';
  const coversProcesses = line === 'pureoa' || line === 'bundle';

  // PUREOA'S TWO AXES (alembic 117). Twin of svdy-website's `planCopy.ts`; the reasoning lives
  // there. Product-line gated for the same reason `advanced_reports` is — an approval-depth limit
  // means nothing to a QuTime-only buyer.
  if (coversProcesses) {
    const levels = plan.max_approval_levels ?? 0;
    labels.push(
      levels > 0
        ? t('common:planCard.features.approvalLevels', { count: levels })
        : t('common:planCard.features.approvalLevelsUnlimited'),
    );
    if (plan.process_escalation) {
      labels.push(t('common:planCard.features.overdueEscalation'));
    }
  }

  // PRODUCT-LINE GATED. `advanced_reports` gates ATTENDANCE reports only —
  // `enforce_advanced_reports` has exactly one call site, in `app/attendance/service.py`, keyed
  // on the report's own type. PureOA's own analytics (`GET /processes/analytics`, bottlenecks
  // included) has no tier gate at all, so printing this on a PureOA card sold the buyer a
  // feature of the product they did not buy.
  if (plan.advanced_reports && coversAttendance) {
    labels.push(t('common:planCard.features.advancedReports'));
  }
  // NO INTEGRATION COUNT. `max_integrations` (0/3/10/999 by tier) has zero enforcement
  // points in the backend — and the cap was never the point: there are SIX connectable
  // integrations in the product, and per-provider `min_tier` narrows each tier further, so
  // starter can reach exactly one (Slack). Every cap is unreachable, which makes publishing
  // the number the false part rather than the missing gate. Twin: svdy-website
  // src/views/Pricing/planCopy.ts.
  if (plan.api_access_level === 'full') {
    labels.push(t('common:planCard.features.apiFull'));
  } else if (plan.api_access_level === 'read') {
    labels.push(t('common:planCard.features.apiRead'));
  }
  // SPLIT by migration 112 — see svdy-website/src/views/Pricing/planCopy.ts, the twin.
  if (plan.sso_enabled) labels.push(t('common:planCard.features.sso'));
  if (plan.scim_enabled) labels.push(t('common:planCard.features.scim'));
  // NO MULTI-CURRENCY LINE. `Organization.currency` is a SINGLE `String(3)` column, so an
  // org has exactly one currency and "multi-currency" is not a state this schema can
  // represent. The `multi_currency` flag had no reader anywhere but this bullet — it was a
  // claim whose only consumer was itself. The column stays (operator-visible, and a future
  // billing slice may need it); what goes is publishing it as a feature.
  if (plan.whitelabel) labels.push(t('common:planCard.features.whiteLabel'));

  const supportTier = plan.support_tier ?? 'community';
  labels.push(
    t(`common:planCard.features.support.${SUPPORT_SHAPE[supportTier] ?? 'standard'}`, {
      hours: SUPPORT_RESPONSE_HOURS[supportTier] ?? SUPPORT_RESPONSE_HOURS.community,
    }),
  );

  return labels;
}


/**
 * ADR-036 — per-extra-worker monthly overage rate for a plan in the given
 * currency (reads prices[ccy].per_extra_worker_monthly, falls back to USD).
 * Returns null when overage is disabled (free / enterprise) → the card shows a
 * plain "Up to N" / "Unlimited" line instead of "Includes N, then $X".
 */
export function planOverageRate(plan: Plan, currency = 'USD'): number | null {
  const prices = (plan.prices ?? {}) as Record<string, Record<string, unknown> | undefined>;
  const entry = prices[currency] ?? prices.USD ?? {};
  const raw = entry?.per_extra_worker_monthly;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}
/**
 * Canonical plan cards always derive localized capabilities from structured
 * fields. Legacy/custom plans may retain backend marketing bullets because
 * they do not have a stable locale-key contract.
 */


export function getPlanBullets(plan: Plan, t: PlanTranslator): string[] {
  if (isCanonicalPlan(plan)) return getPlanFeatureLabels(plan, t);

  const f = plan.features as { marketing_bullets?: unknown } | unknown[] | null | undefined;
  const bullets =
    f && !Array.isArray(f) ? (f as { marketing_bullets?: unknown }).marketing_bullets : undefined;
  if (Array.isArray(bullets) && bullets.length > 0) {
    const strs = bullets.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
    if (strs.length > 0) return strs;
  }
  return getPlanFeatureLabels(plan, t);
}


// ===========================================================================
// Canonical plan filter + tier-based sort
// ===========================================================================
//
// Single source of truth for "which plans does the UI show, and in what
// order". Used by:
//   - svdy-platform-web /pricing (svdy-website actually, but pattern matches)
//   - svdy-platform-web /system/billing Change Plan dialog
//   - svdy-platform-web TopNav Upgrade dialog
//
// alembic 031 seeds 12 canonical plans (3 product lines × 4 tiers).
// All UI surfaces should use these helpers — never sort by price_monthly
// (Enterprise has price=null which collapses to 0 with parseFloat,
// putting Enterprise at position 2 next to Free in stable sort).

const TIER_ORDER: Record<string, number> = {
  free: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
};

/** Filter to canonical 12 plans only (qutime|pureoa|bundle × free|starter|professional|enterprise). */
export function isCanonicalPlan(plan: { code?: string | null; is_active?: boolean }): boolean {
  if (plan.is_active === false) return false;
  return /^(qutime|pureoa|bundle)_(free|starter|professional|enterprise)$/.test(
    plan.code ?? '',
  );
}

/** Sort plans by tier order (Free → Starter → Professional → Enterprise).
 *  Tier-based instead of price-based to handle Enterprise (price=null) correctly. */
export function sortPlansByTier<T extends { tier?: string | null }>(plans: T[]): T[] {
  return [...plans].sort(
    (a, b) =>
      (TIER_ORDER[a.tier ?? ''] ?? 99) - (TIER_ORDER[b.tier ?? ''] ?? 99),
  );
}

/** Derive product line from licensed_products: 1 product → that product, 2+ → bundle. */
export function getProductLine(plan: { licensed_products?: string[] | null }): 'qutime' | 'pureoa' | 'bundle' {
  const products = plan.licensed_products ?? [];
  if (products.length >= 2) return 'bundle';
  if (products[0] === 'pureoa') return 'pureoa';
  return 'qutime';
}


// ---------------------------------------------------------------------------
// Plan chip — color + label helpers (svdy-rules §10.5 single source of truth)
// ---------------------------------------------------------------------------
//
// 2026-05-22 polish — color-coded plan chips matching the role-chip pattern.
// Pre-fix every plan rendered as the same monochrome outlined chip; an
// operator scanning a list of 50 orgs couldn't tell which were revenue
// tiers vs free-tier signups.
//
// Color scheme by ascending value:
//   free         → 'default' (grey)   — no MRR, baseline
//   starter      → 'info'    (blue)   — entry paid tier
//   professional → 'primary' (brand)  — main MRR tier
//   enterprise   → 'success' (green)  — premium MRR tier
//
// Plan codes follow `<line>_<tier>` (qutime_starter / bundle_enterprise);
// plan_code may also be a legacy bare tier (e.g. just 'pro') for orgs
// pre-dating alembic 031. We extract the tier suffix and fall back to
// 'default' for unknown shapes.

export type PlanTier = 'free' | 'starter' | 'professional' | 'enterprise';

/** Extract the canonical tier from a plan code. Returns null when the
 *  code doesn't match `<line>_<tier>` or a bare canonical tier. */
export function extractPlanTier(planCode: string | null | undefined): PlanTier | null {
  if (!planCode) return null;
  // Last underscore-separated segment, OR the whole code if no underscore.
  const segments = planCode.split('_');
  const candidate = segments[segments.length - 1].toLowerCase();
  if (
    candidate === 'free' ||
    candidate === 'starter' ||
    candidate === 'professional' ||
    candidate === 'enterprise'
  ) {
    return candidate;
  }
  return null;
}

/** Map plan code → MUI Chip color. Defaults to 'default' (grey) for
 *  unknown / legacy / null codes so the chip still renders without
 *  visual confusion. */
export function planChipColor(
  planCode: string | null | undefined,
): 'default' | 'info' | 'primary' | 'success' {
  const tier = extractPlanTier(planCode);
  switch (tier) {
    case 'free':
      return 'default';
    case 'starter':
      return 'info';
    case 'professional':
      return 'primary';
    case 'enterprise':
      return 'success';
    default:
      return 'default';
  }
}

/** Display-friendly plan label. Examples:
 *    'bundle_enterprise'       → 'Bundle · Enterprise'
 *    'pureoa_professional'     → 'PureOA · Professional'
 *    'qutime_free'             → 'QuTime · Free'
 *    'pro' (legacy)            → 'Pro'
 *    null                      → '—'
 */
export function planChipLabel(planCode: string | null | undefined): string {
  if (!planCode) return '—';
  const segments = planCode.split('_');
  if (segments.length === 1) {
    // Legacy bare tier — just title-case it.
    return segments[0].charAt(0).toUpperCase() + segments[0].slice(1);
  }
  const lineCode = segments[0].toLowerCase();
  const tierCode = segments[segments.length - 1].toLowerCase();
  const lineLabel =
    lineCode === 'qutime'
      ? 'QuTime'
      : lineCode === 'pureoa'
        ? 'PureOA'
        : lineCode === 'bundle'
          ? 'Bundle'
          : segments[0].charAt(0).toUpperCase() + segments[0].slice(1);
  const tierLabel = tierCode.charAt(0).toUpperCase() + tierCode.slice(1);
  return `${lineLabel} · ${tierLabel}`;
}
